import React, { useEffect, useMemo, useState } from 'react';
import api, { API } from '@/services/api';
import { cn } from '@/lib/utils';

function useQuery() {
  return new URLSearchParams(window.location.search);
}

export default function TVDisplay() {
  const q = useQuery();
  const dealershipId = q.get('dealership_id');
  const token = q.get('token');
  const [data, setData] = useState(null);
  const [screen, setScreen] = useState(0);
  const [error, setError] = useState('');

  const screens = useMemo(() => ([
    { id: 'leaderboard', seconds: 15 },
    { id: 'wins', seconds: 10 },
    { id: 'stats', seconds: 5 },
  ]), []);

  useEffect(() => {
    let t;
    const tick = () => {
      setScreen((s) => (s + 1) % screens.length);
      t = setTimeout(tick, screens[(screen + 1) % screens.length].seconds * 1000);
    };
    t = setTimeout(tick, screens[0].seconds * 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    if (!dealershipId || !token) {
      setError('Missing dealership_id or token');
      return;
    }
    try {
      const res = await fetch(`${API.replace('/api','')}/api/tv/public?dealership_id=${encodeURIComponent(dealershipId)}&token=${encodeURIComponent(token)}`);
      if (!res.ok) throw new Error('Invalid token');
      const json = await res.json();
      setData(json);
      setError('');
    } catch (e) {
      setError('TV access denied');
    }
  };

  useEffect(() => {
    fetchData();
    const i = setInterval(fetchData, 30000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealershipId, token]);

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-bold mb-2">TV Display</div>
          <div className="text-sm opacity-80">{error}</div>
        </div>
      </div>
    );
  }

  const sid = screens[screen]?.id;
  return (
    <div className="min-h-screen bg-neutral-950 text-white p-10">
      <div className="flex items-center justify-center mb-8">
        <div className="text-center">
          <div className="text-3xl font-extrabold tracking-tight">{data?.dealership?.name || 'Dealership'}</div>
          <div className="text-sm opacity-70">Showroom TV</div>
        </div>
      </div>

      {sid === 'leaderboard' && (
        <div>
          <div className="text-2xl font-bold text-center mb-6">THIS MONTH'S LEADERBOARD</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {(data?.leaderboard || []).slice(0, 8).map((r) => (
              <div key={r.user_id} className={cn("rounded-2xl border border-white/10 p-5", r.rank === 1 && "border-amber-400/60")}>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-black">#{r.rank} {r.name}</div>
                  <div className="text-xl font-bold">{r.deals_closed} deals</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sid === 'wins' && (
        <div>
          <div className="text-2xl font-bold text-center mb-6">WIN FEED</div>
          <div className="max-w-4xl mx-auto space-y-4">
            {(data?.wins || []).slice(0, 5).map((w) => (
              <div key={w.id} className="rounded-2xl border border-white/10 p-5 text-xl font-semibold">
                <span className="mr-2">{w.icon}</span>
                {w.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {sid === 'stats' && (
        <div>
          <div className="text-2xl font-bold text-center mb-6">TODAY'S NUMBERS</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            <Stat label="Deals Closed" value={(data?.team_today || []).reduce((a, r) => a + (r.deals_closed || 0), 0)} />
            <Stat label="Appts Booked" value={(data?.team_today || []).reduce((a, r) => a + (r.appointments_booked || 0), 0)} />
            <Stat label="AI Handled" value={(data?.team_today || []).reduce((a, r) => a + (r.ai_handled || 0), 0)} />
            <Stat label="Team Score" value={(data?.team_today || []).reduce((a, r) => a + (r.score || 0), 0)} />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 p-6 text-center">
      <div className="text-sm opacity-70">{label}</div>
      <div className="text-4xl font-black mt-2">{value}</div>
    </div>
  );
}

